import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Box,
  Chip,
  Button,
  Paper,
  Divider,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Slide,
  Tooltip,
  useScrollTrigger,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  Menu as MenuIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Close as CloseIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { ColorModeContext } from '../App';
import MarkdownRenderer from '../components/MarkdownRenderer';
import Footer from '../components/Footer';
import ErrorBoundary from '../components/ErrorBoundary';
import ddosProtection from '../utils/ddosProtection';

// Make ddosProtection globally accessible for testing
if (typeof window !== 'undefined') {
  window.ddosProtection = ddosProtection;
}

const drawerWidth = 200;

// Styled Components
const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  height: 70,
  display: 'flex',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
}));

const NavButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  color: theme.palette.text.secondary,
  position: 'relative',
  fontSize: '0.85rem',
  fontWeight: 500,
  letterSpacing: '0.02em',
  textTransform: 'capitalize',
  padding: theme.spacing(0.5, 1),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: 2,
    background: theme.palette.primary.main,
    opacity: 0,
    transform: 'scaleX(0)',
    transformOrigin: 'center',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    '&::after': {
      opacity: 0.7,
      transform: 'scaleX(0.5)',
    },
  },
  '&.active': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&::after': {
      opacity: 1,
      transform: 'scaleX(0.5)',
    },
  },
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  letterSpacing: '0.02em',
  fontSize: '1.1rem',
  color: theme.palette.text.primary,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -14,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

const ThemeToggleButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  borderRadius: '50%',
  marginLeft: theme.spacing(1),
  padding: theme.spacing(1),
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.03)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-2px)',
  },
}));

const ScrollTopButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  width: 32,
  height: 32,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.secondary.main,
  },
  zIndex: 1000,
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    backgroundColor: theme.palette.background.default,
    borderLeft: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 0),
  }
}));

const MobileNavItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  },
  '&.active': {
    backgroundColor: 'transparent',
    color: theme.palette.secondary.main,
  },
}));

const MobileNavText = styled(ListItemText)(({ theme, active }) => ({
  '& .MuiTypography-root': {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontSize: '0.95rem',
    letterSpacing: '0.01em',
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    fontWeight: active ? 600 : 500,
    textTransform: 'none',
  },
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(1),
  fontSize: '1rem',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.palette.secondary.main,
    transform: 'translateY(-2px)',
    backgroundColor: 'transparent',
  },
}));

function ScrollTop(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Slide direction="up" in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2 }}
      >
        {children}
      </Box>
    </Slide>
  );
}

// Simple frontmatter parser to avoid webpack issues
const parseFrontmatter = (content) => {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);
  
  if (!match) {
    return { data: {}, content };
  }
  
  const frontmatterText = match[1];
  const markdownContent = match[2];
  
  // Parse YAML-like frontmatter
  const data = {};
  frontmatterText.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();
      
      // Remove quotes
      if ((value.startsWith('"') && value.endsWith('"')) || 
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      
      // Parse arrays
      if (value.startsWith('[') && value.endsWith(']')) {
        value = value.slice(1, -1).split(',').map(item => 
          item.trim().replace(/["']/g, '')
        );
      }
      
      data[key] = value;
    }
  });
  
  return { data, content: markdownContent };
};

const BlogPost = () => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [post, setPost] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [rateLimitWarning, setRateLimitWarning] = useState(false);
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlogPost();
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      setError(null);
      setRateLimitWarning(false);

      // Check DDoS protection stats
      const stats = ddosProtection.getStats();
      if (stats.isBlocked) {
        setRateLimitWarning(true);
        setError('Too many requests. Please wait before trying again.');
        setLoading(false);
        return;
      }

      // Fetch blog index with protection
      const indexData = await ddosProtection.fetchWithRetry('/blogs/blogs-index.json');
      const parsedIndex = JSON.parse(indexData);
      const foundPost = parsedIndex.posts.find(p => p.slug === slug);
      
      if (!foundPost) {
        setError('Post not found');
        setLoading(false);
        return;
      }

      // Fetch the markdown file with protection
      const postContent = await ddosProtection.fetchWithRetry(`/blogs/${foundPost.slug}.md`);
      const { data, content: markdownContent } = parseFrontmatter(postContent);
      
      setPost({ ...foundPost, ...data });
      setContent(markdownContent);
    } catch (error) {
      console.error('Error fetching blog post:', error);
      
      if (error.message.includes('Request blocked') || error.message.includes('Rate limit') || error.message.includes('Too Many Requests')) {
        setRateLimitWarning(true);
        setError('Too many requests detected. Please wait a moment before trying again.');
      } else if (error.message.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        setError('Failed to load post. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchBlogPost();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback to copying URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      // You could show a toast notification here
    }
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const menuItems = [
    { text: 'Home', path: '/' },
    { text: 'About', path: '/#about' },
    { text: 'Projects', path: '/#projects' },
    { text: 'Skills', path: '/#skills' },
    { text: 'Contact', path: '/#contact' },
    { text: 'Blog', path: '/blog' },
  ];

  if (loading) {
    return (
      <ErrorBoundary>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
          <StyledAppBar position="fixed">
            <Container maxWidth="lg">
              <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: '100%', minHeight: '60px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Logo onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                    <span style={{color:theme.palette.secondary.main}}>/</span>KumarAv1nash
                  </Logo>
                </Box>
              </Toolbar>
            </Container>
          </StyledAppBar>
          <Main>
            <Toolbar sx={{ minHeight: '60px !important' }} />
            <Container maxWidth="lg" sx={{ py: 6, flexGrow: 1, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Loading post...
              </Typography>
              {rateLimitWarning && (
                <Alert severity="warning" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
                  Loading may take longer due to rate limiting
                </Alert>
              )}
            </Container>
          </Main>
        </Box>
      </ErrorBoundary>
    );
  }

  if (error || !post) {
    return (
      <ErrorBoundary>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
          <StyledAppBar position="fixed">
            <Container maxWidth="lg">
              <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: '100%', minHeight: '60px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Logo onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                    <span style={{color:theme.palette.secondary.main}}>/</span>KumarAv1nash
                  </Logo>
                </Box>
              </Toolbar>
            </Container>
          </StyledAppBar>
          <Main>
            <Toolbar sx={{ minHeight: '60px !important' }} />
            <Container maxWidth="lg" sx={{ py: 6, flexGrow: 1, textAlign: 'center' }}>
              {rateLimitWarning && (
                <Alert severity="warning" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                  Too many requests detected. Please wait a few minutes before trying again.
                </Alert>
              )}
              <Typography variant="h4" gutterBottom color={rateLimitWarning ? 'warning.main' : 'error'}>
                {error || 'Post Not Found'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                {!rateLimitWarning && retryCount < 3 && (
                  <Button variant="outlined" onClick={handleRetry}>
                    Try Again
                  </Button>
                )}
                <Button variant="contained" onClick={() => navigate('/blog')}>
                  Back to Blog
                </Button>
              </Box>
            </Container>
          </Main>
        </Box>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header */}
        <StyledAppBar position="fixed">
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: '100%', minHeight: '60px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Logo onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                  <span style={{color:theme.palette.secondary.main}}>/</span>KumarAv1nash
                </Logo>
              </Box>
              
              {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <NavButton onClick={() => navigate('/')}>Home</NavButton>
                  <NavButton onClick={() => navigate('/#about')}>About</NavButton>
                  <NavButton onClick={() => navigate('/#projects')}>Projects</NavButton>
                  <NavButton onClick={() => navigate('/#skills')}>Skills</NavButton>
                  <NavButton onClick={() => navigate('/#contact')}>Contact</NavButton>
                  <NavButton className="active" onClick={() => navigate('/blog')}>Blog</NavButton>
                  <Tooltip title={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                    <ThemeToggleButton onClick={colorMode.toggleColorMode} aria-label="toggle dark/light mode">
                      {theme.palette.mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                    </ThemeToggleButton>
                  </Tooltip>
                </Box>
              )}
              
              {isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                    <ThemeToggleButton onClick={colorMode.toggleColorMode} aria-label="toggle dark/light mode">
                      {theme.palette.mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                    </ThemeToggleButton>
                  </Tooltip>
                  <IconButton
                    edge="end"
                    color="inherit"
                    aria-label="menu"
                    onClick={handleDrawerOpen}
                    sx={{ display: { sm: 'block', md: 'none' }, ml: 1 }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Box>
              )}
            </Toolbar>
          </Container>
        </StyledAppBar>
        
        {/* Mobile Drawer */}
        <MobileDrawer
          anchor="right"
          open={open}
          onClose={handleDrawerClose}
          variant="temporary"
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, alignItems: 'center' }}>
            <Logo>Avinash Kumar</Logo>
            <IconButton onClick={handleDrawerClose} sx={{ color: 'text.secondary', padding: 0 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider sx={{ my: 1, borderColor: 'divider' }} />
          <List sx={{ mb: 'auto' }}>
            {menuItems.map((item, index) => (
              <MobileNavItem
                button
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  handleDrawerClose();
                }}
                className={item.text === 'Blog' ? 'active' : ''}
              >
                <MobileNavText 
                  primary={item.text}
                  active={item.text === 'Blog'}
                />
              </MobileNavItem>
            ))}
          </List>
          <Divider sx={{ my: 1, borderColor: 'divider' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Box sx={{ display: 'flex' }}>
              <Tooltip title="GitHub">
                <SocialIcon size="small" href="https://github.com/kumarav1nash" target="_blank">
                  <GitHubIcon sx={{ fontSize: '1rem' }} />
                </SocialIcon>
              </Tooltip>
              <Tooltip title="LinkedIn">
                <SocialIcon size="small" href="https://www.linkedin.com/in/kumarav1nash/" target="_blank">
                  <LinkedInIcon sx={{ fontSize: '1rem' }} />
                </SocialIcon>
              </Tooltip>
            </Box>
          </Box>
        </MobileDrawer>
        
        {/* Main Content */}
        <Main>
          <Toolbar sx={{ minHeight: '60px !important' }} />
          <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
            {/* Back Button */}
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/blog')}
              sx={{ mb: 2 }}
            >
              Back to Blog
            </Button>

            {/* Minimal Post Header */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 2 }}>
                {post.title}
              </Typography>

              {/* Minimal Meta Information */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  {post.author}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  {formatDate(post.date)}
                </Typography>
              </Box>

              {/* Minimal Tags */}
              {post.tags && post.tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {post.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/blog?tag=${tag}`)}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem', height: '24px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />
            <Paper elevation={0} sx={{ p: 4 }}>
              <MarkdownRenderer content={content} />
            </Paper>

            {/* Post Footer */}
            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Divider sx={{ mb: 4 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Thanks for reading! If you enjoyed this post, feel free to share it.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/blog')}
              >
                Read More Posts
              </Button>
            </Box>
          </Container>
          
          {/* Footer */}
          <Footer />
        </Main>
        
        {/* Scroll to Top */}
        <ScrollTop>
          <ScrollTopButton size="small">
            <KeyboardArrowUpIcon fontSize="small" />
          </ScrollTopButton>
        </ScrollTop>
      </Box>
    </ErrorBoundary>
  );
};

export default BlogPost;